import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, CheckCircle, Clock, AlertCircle, Download, 
  Pen, Eye, X, Shield, FileSignature, Printer
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LegalAgreementsSection = ({ user }) => {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [documentContent, setDocumentContent] = useState('');
  const [showSignModal, setShowSignModal] = useState(false);
  const [signing, setSigning] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    if (user?.id) {
      fetchAgreements();
      fetchTemplates();
    }
  }, [user?.id]);

  const fetchAgreements = async () => {
    try {
      const res = await axios.get(`${API}/legal-agreements/business/${user.id}`);
      setAgreements(res.data.agreements || []);
    } catch (error) {
      console.error('Error fetching agreements:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/legal-agreements/templates`);
      setTemplates(res.data.templates || []);
      setModalities(res.data.modalities || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const viewAgreement = async (agreement) => {
    try {
      const res = await axios.get(`${API}/legal-agreements/${agreement.agreement_id}/document`);
      setDocumentContent(res.data.content);
      setSelectedAgreement(agreement);
    } catch (error) {
      toast.error('Error al cargar el documento');
    }
  };

  const openSignModal = (agreement) => {
    viewAgreement(agreement);
    setShowSignModal(true);
    setAcceptTerms(false);
  };

  const signAgreement = async () => {
    if (!acceptTerms) {
      toast.error('Debes aceptar los términos para firmar');
      return;
    }

    setSigning(true);
    try {
      await axios.post(`${API}/legal-agreements/sign`, {
        agreement_id: selectedAgreement.agreement_id,
        signer_name: user.name || user.businessName,
        signer_email: user.email,
        accept_terms: true
      });
      toast.success('Acuerdo firmado correctamente');
      setShowSignModal(false);
      setSelectedAgreement(null);
      fetchAgreements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al firmar el acuerdo');
    } finally {
      setSigning(false);
    }
  };

  const downloadDocument = () => {
    if (!documentContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(documentContent);
    printWindow.document.close();
    printWindow.print();
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { icon: Clock, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', label: 'Pendiente de firma' },
      signed: { icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', label: 'Firmado' },
      expired: { icon: AlertCircle, color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', label: 'Expirado' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
        <Icon className="w-4 h-4" />
        {badge.label}
      </span>
    );
  };

  const getAgreementTypeName = (type) => {
    const names = {
      dpa: 'Acuerdo de Procesamiento de Datos (DPA)',
      partnership: 'Acuerdo de Partnership'
    };
    return names[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-local-sage" />
      </div>
    );
  }

  const hasDPA = agreements.some(a => a.type === 'dpa' && a.status === 'signed');
  const hasPartnership = agreements.some(a => a.type === 'partnership' && a.status === 'signed');
  const pendingAgreements = agreements.filter(a => a.status === 'pending');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-manrope font-bold text-2xl flex items-center gap-2">
            <FileSignature className="w-7 h-7 text-local-sage" />
            Acuerdos Legales
          </h2>
          <p className="text-muted-foreground mt-1">
            Documentos legales necesarios para operar con LokAlGrow
          </p>
        </div>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className={`p-4 rounded-xl border ${hasDPA ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800' : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${hasDPA ? 'bg-emerald-500' : 'bg-amber-500'}`}>
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-semibold">DPA (Protección de Datos)</p>
              <p className="text-sm text-muted-foreground">
                {hasDPA ? 'Firmado y activo' : 'Pendiente de firma'}
              </p>
            </div>
            {hasDPA ? (
              <CheckCircle className="w-6 h-6 text-emerald-500 ml-auto" />
            ) : (
              <Clock className="w-6 h-6 text-amber-500 ml-auto" />
            )}
          </div>
        </div>

        <div className={`p-4 rounded-xl border ${hasPartnership ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800' : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${hasPartnership ? 'bg-emerald-500' : 'bg-amber-500'}`}>
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-semibold">Acuerdo de Partnership</p>
              <p className="text-sm text-muted-foreground">
                {hasPartnership ? 'Firmado y activo' : 'Pendiente de firma'}
              </p>
            </div>
            {hasPartnership ? (
              <CheckCircle className="w-6 h-6 text-emerald-500 ml-auto" />
            ) : (
              <Clock className="w-6 h-6 text-amber-500 ml-auto" />
            )}
          </div>
        </div>
      </div>

      {/* Pending Agreements Alert */}
      {pendingAgreements.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-300">
                Tienes {pendingAgreements.length} acuerdo(s) pendiente(s) de firma
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                Para comenzar a usar los servicios de reactivación, debes firmar los acuerdos legales.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Agreements List */}
      <div className="space-y-4">
        <h3 className="font-semibold text-lg">Mis Acuerdos</h3>
        
        {agreements.length === 0 ? (
          <div className="text-center py-12 bg-card rounded-xl border border-border">
            <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No tienes acuerdos generados aún</p>
            <p className="text-sm text-muted-foreground mt-1">
              Los acuerdos se generarán automáticamente cuando contrates servicios
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {agreements.map((agreement) => (
              <motion.div
                key={agreement.agreement_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-card rounded-xl border border-border p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-local-sage/10 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-local-sage" />
                    </div>
                    <div>
                      <p className="font-semibold">{getAgreementTypeName(agreement.type)}</p>
                      <p className="text-sm text-muted-foreground">
                        ID: {agreement.agreement_id}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Creado: {new Date(agreement.created_at).toLocaleDateString('es-ES')}
                        {agreement.signed_at && (
                          <> · Firmado: {new Date(agreement.signed_at).toLocaleDateString('es-ES')}</>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {getStatusBadge(agreement.status)}
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => viewAgreement(agreement)}
                        data-testid={`view-agreement-${agreement.agreement_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                      
                      {agreement.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => openSignModal(agreement)}
                          className="bg-local-sage hover:bg-local-sage/90"
                          data-testid={`sign-agreement-${agreement.agreement_id}`}
                        >
                          <Pen className="w-4 h-4 mr-1" />
                          Firmar
                        </Button>
                      )}
                      
                      {agreement.status === 'signed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            viewAgreement(agreement);
                            setTimeout(downloadDocument, 500);
                          }}
                          data-testid={`download-agreement-${agreement.agreement_id}`}
                        >
                          <Printer className="w-4 h-4 mr-1" />
                          Imprimir
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Document Viewer Modal */}
      <AnimatePresence>
        {selectedAgreement && !showSignModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedAgreement(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-border">
                <div>
                  <h3 className="font-bold text-lg">{getAgreementTypeName(selectedAgreement.type)}</h3>
                  <p className="text-sm text-muted-foreground">{selectedAgreement.agreement_id}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={downloadDocument}>
                    <Printer className="w-4 h-4 mr-1" />
                    Imprimir
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedAgreement(null)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>
              <div 
                className="p-6 overflow-auto max-h-[calc(90vh-80px)]"
                ref={printRef}
              >
                <div 
                  className="prose dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: documentContent }}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sign Agreement Modal */}
      <AnimatePresence>
        {showSignModal && selectedAgreement && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-xl"
            >
              <div className="flex items-center justify-between p-4 border-b border-border bg-local-sage/5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-local-sage flex items-center justify-center">
                    <FileSignature className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Firmar Acuerdo</h3>
                    <p className="text-sm text-muted-foreground">{getAgreementTypeName(selectedAgreement.type)}</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => {
                  setShowSignModal(false);
                  setSelectedAgreement(null);
                }}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Document Preview */}
              <div className="p-4 overflow-auto max-h-[50vh] bg-gray-50 dark:bg-gray-800/50">
                <div 
                  className="prose dark:prose-invert max-w-none text-sm"
                  dangerouslySetInnerHTML={{ __html: documentContent }}
                />
              </div>

              {/* Signature Section */}
              <div className="p-6 border-t border-border space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <strong>Firmante:</strong> {user?.name || user?.businessName}<br />
                    <strong>Email:</strong> {user?.email}<br />
                    <strong>Fecha:</strong> {new Date().toLocaleDateString('es-ES', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="accept-terms"
                    checked={acceptTerms}
                    onCheckedChange={setAcceptTerms}
                    data-testid="accept-terms-checkbox"
                  />
                  <label htmlFor="accept-terms" className="text-sm cursor-pointer">
                    He leído y acepto los términos y condiciones del presente acuerdo. 
                    Entiendo que esta firma digital tiene la misma validez legal que una firma manuscrita.
                  </label>
                </div>

                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowSignModal(false);
                      setSelectedAgreement(null);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={signAgreement}
                    disabled={!acceptTerms || signing}
                    className="bg-local-sage hover:bg-local-sage/90"
                    data-testid="confirm-sign-btn"
                  >
                    {signing ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                        Firmando...
                      </>
                    ) : (
                      <>
                        <Pen className="w-4 h-4 mr-2" />
                        Firmar Digitalmente
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
